import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MODES } from './modes';
import { renderMarkdown } from './markdown';
import FlashcardDeck from './FlashcardDeck';
import DsaCard from './DsaCard';
import McqCard from './McqCard';
import { STARTER, buildDsaPrompt } from './dsaProblem';
import { MCQS } from './mcqData';
import APIService from '../../services/APIService';
import {
  IconHistory, IconChevDown, IconSend, IconStop, IconMic, IconPaperclip,
  IconArrowRight, IconCards,
} from './icons';
import { logAsked, scopeIds } from './memoryLog';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const now = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const uid = (p) => `${p}-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

/*
 * NanoChat — ONE continuous conversation for every mode.
 * Each DSA entry appends a NEW inline coding card (its own code/lang); the
 * previous DSA card freezes to read-only, so the thread keeps a history of
 * attempts. Flashcards render as inline decks, the interviewer as an inline
 * launcher, and mcq/theory as streamed text — all in the same thread.
 */
const NanoChat = ({ mode, config, onLaunchVoice, registerSend, registerMcq }) => {
  const windowMode = MODES[mode];                          // the window (topbar + greeting)
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const activeId = mode;                                   // effective content mode
  const activeMode = MODES[activeId];

  const msgsEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const taRef = useRef(null);
  const mountedRef = useRef(true);
  const prevActiveRef = useRef(activeId);
  const didMountRef = useRef(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const sendRef = useRef(null);

  useEffect(() => {
    msgsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; abortRef.current?.abort(); };
  }, []);

  // Shared streaming primitive — streams /chat tokens into message `aiId`.
  const streamInto = useCallback(async (aiId, promptText) => {
    setStreaming(true);
    abortRef.current = new AbortController();
    try {
      const resp = await fetch(`${API_BASE_URL}/chat?q=${encodeURIComponent(promptText)}`, { signal: abortRef.current.signal });
      if (!resp.ok || !resp.body) throw new Error(`Request failed (${resp.status})`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          let token;
          try { token = JSON.parse(payload); } catch { token = payload; }
          if (mountedRef.current) setMessages((prev) => prev.map((m) => (m.id === aiId ? { ...m, content: m.content + token } : m)));
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError' && mountedRef.current) {
        setMessages((prev) => prev.map((m) => (m.id === aiId
          ? { ...m, content: m.content || '⚠️ Could not reach the AI. Is the backend running?', error: true } : m)));
      }
    } finally {
      if (mountedRef.current) {
        setMessages((prev) => prev.map((m) => (m.id === aiId ? { ...m, streaming: false } : m)));
        setStreaming(false);
      }
    }
  }, []);

  // Pull a flashcard deck into the thread. Config-driven decks come from the
  // Knowledge Hub via the engine (Q + LLM-generated answer, tagged to hub
  // concepts); with no configured scope it falls back to the subject store.
  const loadDeckInline = useCallback(async (topic) => {
    const fcId = uid('fc');
    setMessages((prev) => [...prev, { role: 'ai', id: fcId, type: 'deck-loading', mode: 'flashcards', time: now() }]);
    try {
      const cids = scopeIds(config);
      let cards = [];
      if (cids.length) {
        const res = await APIService.engineFlashcards({ conceptIds: cids, count: 8 });
        cards = res.cards || [];
      }
      if (!cards.length) {
        const data = await APIService.getFlashcards(null, topic || null);
        cards = data.flashcards || [];
      }
      if (!mountedRef.current) return;
      setMessages((prev) => prev.map((m) => (m.id === fcId
        ? (cards.length
            ? { ...m, type: 'flashcards', cards }
            : { ...m, type: 'ai', content: `I couldn't find flashcards${topic ? ` on "${topic}"` : ''}. Try another topic, or switch modes.` })
        : m)));
    } catch (e) {
      if (!mountedRef.current) return;
      setMessages((prev) => prev.map((m) => (m.id === fcId
        ? { ...m, type: 'ai', content: '⚠️ Could not load flashcards. Is the backend running?' } : m)));
    }
  }, [config]);

  const pushVoiceCta = useCallback(() => {
    setMessages((prev) => [...prev, { role: 'ai', id: uid('vc'), type: 'voice-cta', mode: 'interviewer', time: now() }]);
  }, []);

  // Append a fresh DSA card and freeze any prior ones (read-only history).
  const addDsaCard = useCallback(() => {
    setMessages((prev) => [
      ...prev.map((m) => (m.type === 'dsa' ? { ...m, active: false } : m)),
      { role: 'ai', id: uid('dsa'), type: 'dsa', mode: 'dsa', code: STARTER, lang: 'Python3', active: true, time: now() },
    ]);
  }, []);

  const updateMessage = useCallback((id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  // Post a DSA question (Run/Submit/chips) into the thread using a card's code.
  const askInThread = useCallback((userPrompt, label, code, lang) => {
    if (streaming) return;
    setMessages((prev) => [...prev, { role: 'user', content: label, time: now() }]);
    const aiId = uid('ai');
    setMessages((prev) => [...prev, { role: 'ai', content: '', id: aiId, streaming: true, mode: 'dsa', time: now() }]);
    streamInto(aiId, buildDsaPrompt(userPrompt, code, lang));
  }, [streaming, streamInto]);

  // Post an already-built prompt (e.g. MCQ assistant chips) into the thread.
  const postToThread = useCallback((promptText, label, msgMode) => {
    if (streaming) return;
    setMessages((prev) => [...prev, { role: 'user', content: label, time: now() }]);
    const aiId = uid('ai');
    setMessages((prev) => [...prev, { role: 'ai', content: '', id: aiId, streaming: true, mode: msgMode, time: now() }]);
    streamInto(aiId, promptText);
  }, [streaming, streamInto]);

  // Append a fresh MCQ quiz card and freeze any prior ones (read-only history).
  const addMcqCard = useCallback(() => {
    setMessages((prev) => [
      ...prev.map((m) => (m.type === 'mcq' ? { ...m, active: false } : m)),
      { role: 'ai', id: uid('mcq'), type: 'mcq', mode: 'mcq', active: true, idx: 0, answers: {}, marked: [], time: now() },
    ]);
  }, []);

  // On mount add the active mode's content; in Mix, switching sub-modes appends
  // a divider + the new content (focused windows never change activeId).
  useEffect(() => {
    const firstTopic = config?.subjects?.[0]?.name;
    const enter = (m) => {
      if (m === 'flashcards') loadDeckInline(firstTopic);            // deck scoped to the session config
      else if (m === 'interviewer') pushVoiceCta();
      else if (m === 'dsa') addDsaCard();
      else if (m === 'mcq') addMcqCard();
      else if (m === 'theory') {
        // Open with a question the engine picked for the configured topics.
        const q = (config?.plan?.by_mode?.theory || [])[0];
        const question = q && q.payload && q.payload.question;
        if (question) {
          setMessages((prev) => [...prev, {
            role: 'ai', id: uid('ai'), mode: 'theory', time: now(),
            content: `Let's review **${firstTopic || 'your topics'}**. Here's one to start:\n\n${question}\n\nAnswer in your own words and I'll give feedback.`,
          }]);
        }
      }
    };
    if (!didMountRef.current) {
      didMountRef.current = true;
      prevActiveRef.current = activeId;
      enter(activeId);
      return;
    }
    if (prevActiveRef.current === activeId) return;
    prevActiveRef.current = activeId;
    setMessages((prev) => [...prev, { role: 'system', id: uid('sys'), text: `Switched to ${MODES[activeId].label} mode` }]);
    enter(activeId);
  }, [activeId, loadDeckInline, pushVoiceCta, addDsaCard, addMcqCard]);

  // Surface the active MCQ quiz's progress + navigator to the left sidebar.
  const activeMcq = messages.find((m) => m.type === 'mcq' && m.active);
  useEffect(() => {
    if (!registerMcq) return;
    if (activeId !== 'mcq' || !activeMcq) { registerMcq(null); return; }
    const answers = activeMcq.answers || {};
    const answeredCount = Object.keys(answers).length;
    const correctCount = Object.entries(answers).filter(([k, v]) => MCQS[k].correct === v).length;
    registerMcq({
      idx: activeMcq.idx,
      total: MCQS.length,
      answers,
      marked: activeMcq.marked || [],
      answeredCount,
      correctCount,
      incorrectCount: answeredCount - correctCount,
      unattempted: MCQS.length - answeredCount,
      solvedPct: Math.round((answeredCount / MCQS.length) * 100),
      goTo: (i) => updateMessage(activeMcq.id, { idx: i }),
    });
  }, [activeId, activeMcq, registerMcq, updateMessage]);

  const autoGrow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  };

  const send = useCallback(
    async (rawText) => {
      const text = (rawText ?? input).trim();
      if (!text || streaming) return;

      setInput('');
      if (taRef.current) taRef.current.style.height = 'auto';
      setMessages((prev) => [...prev, { role: 'user', content: text, time: now() }]);

      // Engagement signal for chat-based modes (theory / interviewer / mix);
      // dsa, mcq and flashcards record richer signals of their own.
      if (activeId !== 'dsa' && activeId !== 'mcq' && activeId !== 'flashcards') {
        logAsked(activeId, scopeIds(config), text);
      }

      if (activeId === 'flashcards') { loadDeckInline(text); return; }

      let promptText;
      if (activeId === 'dsa') {
        const activeDsa = messagesRef.current.find((m) => m.type === 'dsa' && m.active);
        promptText = buildDsaPrompt(text, activeDsa?.code ?? STARTER, activeDsa?.lang ?? 'Python3');
      } else {
        promptText = activeMode.buildPrompt(text, config);
      }

      const aiId = uid('ai');
      setMessages((prev) => [...prev, { role: 'ai', content: '', id: aiId, streaming: true, mode: activeId, time: now() }]);
      streamInto(aiId, promptText);
    },
    [input, streaming, activeId, activeMode, config, loadDeckInline, streamInto]
  );

  // Expose send() to the parent so the sidebar context links can drive the chat.
  sendRef.current = send;
  useEffect(() => {
    registerSend?.((t) => sendRef.current?.(t));
    return () => registerSend?.(null);
  }, [registerSend]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const emptyThread = messages.length === 0;

  return (
    <div className="nano-chat">
      {/* Topbar */}
      <div className="nano-chat-topbar">
        <div className="nano-chat-title">
          {windowMode.label} Mode <span className="chev"><IconChevDown size={15} /></span>
        </div>
        <div className="nano-topbar-spacer" />
        <button className="nano-hist-btn" type="button"><IconHistory size={15} /> History</button>
        <div className="nano-mode-pill-top" style={{ background: activeMode.accent }}>
          <activeMode.Icon size={15} /> {activeMode.label}
        </div>
      </div>

      {/* Body: thread · right context */}
      <div className="nano-chat-body">
        <div className="nano-chat-main">
          <div className="nano-msgs">
            {/* Persistent greeting header (reflects the window) */}
            <div className="nano-greet">
              <div className="nano-ai-av" style={{ background: `linear-gradient(135deg, ${windowMode.accent}, #8b5cf6)` }}>
                <windowMode.Icon size={17} />
              </div>
              <div className="nano-greet-bubble">
                <h4>Hi Arjun! 👋</h4>
                <p>{windowMode.greeting}</p>
              </div>
            </div>

            {/* Starter suggestions only while the thread is empty */}
            {emptyThread && (
              <div className="nano-quick-actions" style={{ marginLeft: 46 }}>
                {windowMode.suggestions.map((s) => (
                  <button key={s} className="nano-qa" onClick={() => send(s)}>
                    <IconArrowRight size={13} /> {s}
                  </button>
                ))}
              </div>
            )}

            {/* The one continuous thread */}
            {messages.map((m, i) => {
              if (m.role === 'system') {
                return <div key={m.id || i} className="nano-divider"><span>{m.text}</span></div>;
              }
              const mm = MODES[m.mode] || activeMode;
              const isAi = m.role === 'ai';
              const isRich = m.type === 'flashcards' || m.type === 'deck-loading' || m.type === 'voice-cta' || m.type === 'dsa' || m.type === 'mcq';
              const showQuickActions =
                isAi && !isRich && !m.streaming && i === messages.length - 1 &&
                activeId !== 'flashcards' && activeMode.kind !== 'voice';

              return (
                <div key={m.id || i} className={`nano-msg ${m.role}`}>
                  <div className="nano-ai-av" style={isAi ? { background: `linear-gradient(135deg, ${mm.accent}, #8b5cf6)` } : undefined}>
                    {isAi ? <mm.Icon size={16} /> : 'Me'}
                  </div>
                  <div style={(m.type === 'dsa' || m.type === 'mcq') ? { flex: 1, minWidth: 0 } : undefined}>
                    {m.type === 'deck-loading' ? (
                      <div className="nano-deck-loading">
                        <div className="ico"><IconCards size={18} /></div>
                        <div>
                          <div className="txt">Getting your flashcards ready…</div>
                          <div className="bar"><span /></div>
                        </div>
                      </div>
                    ) : m.type === 'flashcards' ? (
                      <FlashcardDeck cards={m.cards} accent={MODES.flashcards.accent} />
                    ) : m.type === 'dsa' ? (
                      <DsaCard
                        code={m.code}
                        lang={m.lang}
                        active={m.active}
                        onCodeChange={(val) => updateMessage(m.id, { code: val })}
                        onLangChange={(val) => updateMessage(m.id, { lang: val })}
                        onAsk={(p, l) => askInThread(p, l, m.code, m.lang)}
                        disabled={streaming || !m.active}
                      />
                    ) : m.type === 'mcq' ? (
                      <McqCard
                        idx={m.idx}
                        answers={m.answers}
                        marked={m.marked}
                        active={m.active}
                        onChange={(patch) => updateMessage(m.id, patch)}
                        disabled={streaming || !m.active}
                        onAsk={(promptText, label) => postToThread(promptText, label, 'mcq')}
                      />
                    ) : m.type === 'voice-cta' ? (
                      <div className="nano-greet-bubble" style={{ maxWidth: 'none' }}>
                        <div className="nano-voice-launch">
                          <div className="nano-voice-orb"><IconMic size={30} /></div>
                          <h3>Live Voice Interview</h3>
                          <p>Talk to Alex, your AI interviewer, in real time — or just type below to practice in text, right here in the chat.</p>
                          <button className="nano-voice-btn" onClick={onLaunchVoice} type="button">
                            <IconMic size={17} /> Start Voice Interview
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`nano-bubble${m.error ? ' error' : ''}`}>
                        {isAi ? (<>{renderMarkdown(m.content)}{m.streaming && <span className="nano-cursor" />}</>) : m.content}
                      </div>
                    )}

                    {showQuickActions && (
                      <div className="nano-quick-actions">
                        {activeMode.quickActions.map((qa) => (
                          <button key={qa} className="nano-qa" onClick={() => send(qa)}>{qa}</button>
                        ))}
                      </div>
                    )}
                    {m.time && <div className="nano-msg-time">{m.time}</div>}
                  </div>
                </div>
              );
            })}
            <div ref={msgsEndRef} />
          </div>

          {/* Composer */}
          <div className="nano-composer-wrap">
            <div className="nano-composer">
              <button className="nano-attach" type="button" title="Attach"><IconPaperclip size={18} /></button>
              <textarea
                ref={(el) => { taRef.current = el; inputRef.current = el; }}
                placeholder={activeId === 'dsa' ? 'Ask anything about this problem…' : activeMode.placeholder}
                value={input}
                onChange={(e) => { setInput(e.target.value); autoGrow(); }}
                onKeyDown={onKeyDown}
                rows={1}
              />
              <button className="nano-mic" type="button" title="Voice interview" onClick={onLaunchVoice}>
                <IconMic size={18} />
              </button>
              {streaming ? (
                <button className="nano-send stop" onClick={() => abortRef.current?.abort()} type="button" title="Stop">
                  <IconStop size={17} />
                </button>
              ) : (
                <button className="nano-send" onClick={() => send()} disabled={!input.trim()} type="button" title="Send">
                  <IconSend size={17} />
                </button>
              )}
            </div>
            <div className="nano-disclaimer">AI can make mistakes. Please verify important information.</div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default NanoChat;
